/**
 * Facturación API Service
 * CRUD operations for invoices, payments, and SAR compliance
 */

import { api } from "@/lib/api";
import type { PaginatedResponse, FetchParams } from "@/hooks/use-server-data-table";

// =============================================================================
// URLS
// =============================================================================

const FACTURAS_URL = "/facturas/";
const CAIS_URL = "/cais/";
const METODOS_PAGO_URL = "/metodos-pago/";

// =============================================================================
// TYPES - Enums
// =============================================================================

export type EstadoFactura = "BORRADOR" | "EMITIDA" | "PAGADA" | "ANULADA";
export type TipoDocumentoFiscal = "FACTURA" | "RECIBO" | "NOTA_CREDITO";
export type TipoMetodoPago = "EFECTIVO" | "TARJETA" | "TRANSFERENCIA" | "CHEQUE";
export type TipoPago = "ANTICIPO" | "ABONO" | "CANCELACION";

// =============================================================================
// TYPES - MetodoPago
// =============================================================================

export interface MetodoPago {
  id: number;
  nombre: string;
  tipo: TipoMetodoPago;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// TYPES - FacturaDetalle
// =============================================================================

export interface FacturaDetalle {
  id: number;
  examen_tipo: number;
  examen_nombre: string;
  orden_examen: number | null;
  orden_numero: string | null;
  descripcion: string;
  cantidad: number;
  precio_unitario: string;
  descuento: string;
  subtotal: string;
}

// =============================================================================
// TYPES - PagoFactura
// =============================================================================

export interface PagoFactura {
  id: number;
  metodo: number;
  metodo_nombre: string;
  monto: string;
  tipo_pago: TipoPago;
  referencia: string;
  fecha: string;
  created_at: string;
}

export interface PagoResult {
  pago: PagoFactura;
  vuelto: string;
  esta_pagada: boolean;
  saldo_pendiente: string;
}

// =============================================================================
// TYPES - OrdenDisponible
// =============================================================================

export interface OrdenDisponibleExamen {
  id: number;
  examen_tipo_id: number;
  nombre: string;
  precio: string;
}

export interface OrdenDisponible {
  id: number;
  numero: string;
  fecha: string;
  paciente_nombre: string;
  cliente_id: number | null;
  cliente_nombre: string | null;
  cliente_rtn: string | null;
  examenes: OrdenDisponibleExamen[];
  total: string;
  tipo_cobro: string | null;
}

// =============================================================================
// TYPES - Factura
// =============================================================================

export interface FacturaOrdenResumen {
  id: number;
  orden: number;
  numero: string;
  fecha: string;
  total: string;
  lista_precio_nombre: string | null;
  lista_precio_porcentaje: string | null;
  descuento_porcentaje: string;
  descuento_lempiras: string;
}

export interface FacturaCliente {
  id: number;
  nombre: string;
  rtn: string;
}

export interface Factura {
  id: number;
  numero: string;
  cai: number;
  cai_codigo: string;
  cai_rango_inicio: string;
  cai_rango_fin: string;
  cai_fecha_limite: string;
  ordenes: FacturaOrdenResumen[];
  ordenes_count: number;
  cliente: FacturaCliente | null;
  paciente: number;
  paciente_nombre: string;
  paciente_identificacion: string;
  paciente_direccion: string;
  nombre_cliente: string;
  rtn_cliente: string;
  modo_facturacion: "PACIENTE" | "EMPRESA" | "AMBOS";
  sucursal: number;
  sucursal_nombre: string;
  sucursal_direccion: string;
  subtotal: string;
  descuento: string;
  descuento_pagos_directos: string;
  impuesto: string;
  exento: string;
  total: string;
  monto_en_letras: string;
  estado: EstadoFactura;
  tipo_documento: TipoDocumentoFiscal;
  fecha: string;
  observaciones: string;
  qr_verificacion: string;
  detalles: FacturaDetalle[];
  pagos: PagoFactura[];
  esta_pagada: boolean;
  total_pagado: string;
  saldo_pendiente: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FacturaListItem {
  id: number;
  numero: string;
  ordenes_count: number;
  cliente: number | null;
  cliente_nombre: string | null;
  paciente: number;
  paciente_nombre: string;
  nombre_cliente: string;
  rtn_cliente: string;
  sucursal: number;
  sucursal_nombre: string;
  subtotal: string;
  descuento: string;
  descuento_pagos_directos: string;
  total: string;
  estado: EstadoFactura;
  tipo_documento: TipoDocumentoFiscal;
  fecha: string;
  esta_pagada: boolean;
  total_pagado: string;
  saldo_pendiente: string;
  is_active: boolean;
  created_at: string;
}

// =============================================================================
// TYPES - Create/Action DTOs
// =============================================================================

export interface FacturaCreateData {
  orden_ids: number[];
  nombre_cliente?: string;
  rtn_cliente?: string;
  descuento_global?: number;
  observaciones?: string;
  modo_facturacion?: "PACIENTE" | "EMPRESA" | "AMBOS";
}

export interface PagoCreateData {
  metodo: number;
  monto_recibido: number;
  tipo_pago?: TipoPago;
  referencia?: string;
}

// =============================================================================
// API - Facturas
// =============================================================================

export async function getFacturas(
  params?: FetchParams
): Promise<PaginatedResponse<FacturaListItem>> {
  const response = await api.get(FACTURAS_URL, { params });
  return response.data;
}

export async function getFactura(id: number): Promise<Factura> {
  const response = await api.get(`${FACTURAS_URL}${id}/`);
  return response.data;
}

export async function createFactura(data: FacturaCreateData): Promise<Factura> {
  const response = await api.post(FACTURAS_URL, data);
  return response.data;
}

export async function anularFactura(id: number, motivo: string): Promise<Factura> {
  const response = await api.post(`${FACTURAS_URL}${id}/anular/`, { motivo });
  return response.data;
}

export async function registrarPago(id: number, data: PagoCreateData): Promise<PagoResult> {
  const response = await api.post(`${FACTURAS_URL}${id}/pagar/`, data);
  return response.data;
}

export async function getOrdenesDisponibles(params: {
  pacienteId?: number;
  clienteId?: number;
  sucursalId: number;
}): Promise<OrdenDisponible[]> {
  const queryParams: Record<string, number> = { sucursal_id: params.sucursalId };
  if (params.clienteId != null) {
    queryParams.cliente_id = params.clienteId;
  } else if (params.pacienteId != null) {
    queryParams.paciente_id = params.pacienteId;
  }
  const response = await api.get(`${FACTURAS_URL}ordenes-disponibles/`, {
    params: queryParams,
  });
  return response.data;
}

export async function getOrdenPorNumero(
  numero: string,
  sucursalId: number
): Promise<OrdenDisponible[]> {
  const response = await api.get(`${FACTURAS_URL}orden-por-numero/`, {
    params: { numero, sucursal_id: sucursalId },
  });
  return response.data;
}

// =============================================================================
// API - Métodos de Pago
// =============================================================================

export async function getMetodosPago(params?: FetchParams): Promise<PaginatedResponse<MetodoPago>> {
  const response = await api.get(METODOS_PAGO_URL, { params });
  return response.data;
}

// =============================================================================
// CONSTANTS
// =============================================================================

export const ESTADOS_FACTURA: { value: EstadoFactura; label: string }[] = [
  { value: "BORRADOR", label: "Borrador" },
  { value: "EMITIDA", label: "Emitida" },
  { value: "PAGADA", label: "Pagada" },
  { value: "ANULADA", label: "Anulada" },
];

// =============================================================================
// TYPES - CAI
// =============================================================================

export interface CAI {
  id: number;
  cai: string;
  rango_inicio: string;
  rango_fin: string;
  fecha_limite: string;
  sucursal: number;
  sucursal_nombre: string;
  punto_emision: string;
  numero_actual: number;
  esta_vigente: boolean;
  rango_disponible: boolean;
  numeros_restantes: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CAIListItem {
  id: number;
  cai: string;
  rango_inicio: string;
  rango_fin: string;
  fecha_limite: string;
  sucursal: number;
  sucursal_nombre: string;
  punto_emision: string;
  numero_actual: number;
  esta_vigente: boolean;
  numeros_restantes: number;
  is_active: boolean;
  created_at: string;
}

export interface CAICreateData {
  cai: string;
  rango_inicio: string;
  rango_fin: string;
  fecha_limite: string;
  sucursal: number;
  punto_emision: string;
}

// =============================================================================
// API - CAI
// =============================================================================

export async function getCAIs(params?: FetchParams): Promise<PaginatedResponse<CAIListItem>> {
  const response = await api.get(CAIS_URL, { params });
  return response.data;
}

export async function getCAI(id: number): Promise<CAI> {
  const response = await api.get(`${CAIS_URL}${id}/`);
  return response.data;
}

export async function createCAI(data: CAICreateData): Promise<CAI> {
  const response = await api.post(CAIS_URL, data);
  return response.data;
}

export async function updateCAI(id: number, data: CAICreateData): Promise<CAI> {
  const response = await api.put(`${CAIS_URL}${id}/`, data);
  return response.data;
}

export async function deactivateCAI(id: number): Promise<CAI> {
  const response = await api.post(`${CAIS_URL}${id}/deactivate/`);
  return response.data;
}

export async function activateCAI(id: number): Promise<CAI> {
  const response = await api.post(`${CAIS_URL}${id}/activate/`);
  return response.data;
}

export async function deleteCAI(id: number): Promise<void> {
  await api.delete(`${CAIS_URL}${id}/`);
}

// =============================================================================
// URLS - Listas de Precios
// =============================================================================

const LISTAS_PRECIO_URL = "/listas-precio/";
const PRECIOS_EXAMEN_URL = "/precios-examen/";

// =============================================================================
// TYPES - ListaPrecio
// =============================================================================

export type TipoListaPrecio = "PUBLICO" | "FERIADO" | "ALIANZA" | "CORPORATIVO";

export interface ListaPrecio {
  id: number;
  nombre: string;
  tipo: TipoListaPrecio;
  descripcion: string;
  porcentaje_ajuste: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number | null;
  updated_by: number | null;
}

export interface ListaPrecioCreateData {
  nombre: string;
  tipo: TipoListaPrecio;
  descripcion?: string;
  porcentaje_ajuste?: number;
}

// =============================================================================
// TYPES - PrecioExamen
// =============================================================================

export interface PrecioExamen {
  id: number;
  lista: number;
  lista_nombre: string;
  examen_tipo: number;
  examen_nombre: string;
  precio: string;
  vigente_desde: string;
  vigente_hasta: string | null;
  esta_vigente: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PrecioExamenCreateData {
  lista: number;
  examen_tipo: number;
  precio: number;
  vigente_desde: string;
  vigente_hasta?: string | null;
}

// =============================================================================
// CONSTANTS - Listas de Precios
// =============================================================================

export const TIPOS_LISTA_PRECIO: { value: TipoListaPrecio; label: string }[] = [
  { value: "PUBLICO", label: "Público" },
  { value: "FERIADO", label: "Día feriado" },
  { value: "ALIANZA", label: "Alianza comercial" },
  { value: "CORPORATIVO", label: "Corporativo" },
];

// =============================================================================
// API - Listas de Precios
// =============================================================================

export async function getListasPrecios(
  params?: FetchParams
): Promise<PaginatedResponse<ListaPrecio>> {
  const response = await api.get(LISTAS_PRECIO_URL, { params });
  return response.data;
}

export async function getListaPrecio(id: number): Promise<ListaPrecio> {
  const response = await api.get(`${LISTAS_PRECIO_URL}${id}/`);
  return response.data;
}

export async function createListaPrecio(data: ListaPrecioCreateData): Promise<ListaPrecio> {
  const response = await api.post(LISTAS_PRECIO_URL, data);
  return response.data;
}

export async function updateListaPrecio(
  id: number,
  data: ListaPrecioCreateData
): Promise<ListaPrecio> {
  const response = await api.put(`${LISTAS_PRECIO_URL}${id}/`, data);
  return response.data;
}

export async function deactivateListaPrecio(id: number): Promise<ListaPrecio> {
  const response = await api.post(`${LISTAS_PRECIO_URL}${id}/deactivate/`);
  return response.data;
}

export async function activateListaPrecio(id: number): Promise<ListaPrecio> {
  const response = await api.post(`${LISTAS_PRECIO_URL}${id}/activate/`);
  return response.data;
}

export async function deleteListaPrecio(id: number): Promise<void> {
  await api.delete(`${LISTAS_PRECIO_URL}${id}/`);
}

// =============================================================================
// API - Precios Examen
// =============================================================================

export async function getPreciosExamen(
  params?: FetchParams
): Promise<PaginatedResponse<PrecioExamen>> {
  const response = await api.get(PRECIOS_EXAMEN_URL, { params });
  return response.data;
}

export async function createPrecioExamen(data: PrecioExamenCreateData): Promise<PrecioExamen> {
  const response = await api.post(PRECIOS_EXAMEN_URL, data);
  return response.data;
}

export async function updatePrecioExamen(
  id: number,
  data: PrecioExamenCreateData
): Promise<PrecioExamen> {
  const response = await api.put(`${PRECIOS_EXAMEN_URL}${id}/`, data);
  return response.data;
}

export async function deletePrecioExamen(id: number): Promise<void> {
  await api.delete(`${PRECIOS_EXAMEN_URL}${id}/`);
}
